import React from 'react';
import { EyeIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, CalendarIcon, UserIcon, BarChartIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
export function VendorCard({
  vendor,
  onViewVendor,
  getAvailabilityIcon,
  getAvailabilityText,
  locale,
  isESGEnabled = false
}) {
  // Format date based on locale
  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  // Get ESG status label and color
  const getESGStatusInfo = score => {
    if (score >= 80) return {
      label: 'Excellent',
      color: 'green'
    };
    if (score >= 60) return {
      label: 'Good',
      color: 'blue'
    };
    if (score >= 40) return {
      label: 'Moderate',
      color: 'yellow'
    };
    return {
      label: 'Needs Review',
      color: 'red'
    };
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-18907">
      {/* Card Header */}
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-18908">
        <div className="flex justify-between items-start" data-id="element-18909">
          <div data-id="element-18910">
            <h3 className="text-lg leading-6 font-medium text-gray-900 truncate" title={vendor.name} data-id="element-18911">
              {vendor.name}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 truncate" data-id="element-18912">
              {vendor.id}
            </p>
          </div>
          <div data-id="element-18913">
            <StatusBadge status={vendor.status.toLowerCase()} text={vendor.status} data-id="element-18914" />
          </div>
        </div>
      </div>
      {/* Card Body */}
      <div className="px-4 py-5 sm:p-6" data-id="element-18915">
        <div className="grid grid-cols-2 gap-4" data-id="element-18916">
          <div data-id="element-18917">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18918">
              Type
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-18919">
              {vendor.type || 'Not specified'}
            </p>
          </div>
          <div data-id="element-18920">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18921">
              Country
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-18922">{vendor.country}</p>
          </div>
          <div data-id="element-18923">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18924">
              Onboarded
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-18925">
              {formatDate(vendor.onboardingDate)}
            </p>
          </div>
          <div data-id="element-18926">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18927">
              Last Updated
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-18928">
              {formatDate(vendor.lastUpdated)}
            </p>
          </div>
        </div>
        <div className="mt-4" data-id="element-18929">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18930">
            Availability
          </h4>
          <div className="mt-1 flex items-center text-sm text-gray-700" data-id="element-18931">
            {getAvailabilityIcon(vendor)}
            <span className="ml-1" data-id="element-18932">{getAvailabilityText(vendor)}</span>
          </div>
        </div>
        <div className="mt-4" data-id="element-18933">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18934">
            Compliance Status
          </h4>
          <div className="mt-1 flex items-center text-sm" data-id="element-18935">
            {vendor.complianceStatus === 'Compliant' && <CheckCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-green-500" data-id="element-18936" />}
            {vendor.complianceStatus === 'Pending Review' && <AlertTriangleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-yellow-500" data-id="element-18937" />}
            {(vendor.complianceStatus === 'Incomplete' || vendor.complianceStatus === 'Not Started') && <AlertTriangleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-18938" />}
            {vendor.complianceStatus === 'Non-Compliant' && <XCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-red-500" data-id="element-18939" />}
            <span className="text-gray-700" data-id="element-18940">{vendor.complianceStatus}</span>
          </div>
        </div>
        {/* ESG Score - Only show if enabled */}
        {isESGEnabled && <div className="mt-4" data-id="element-18941">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18942">
              ESG Score
            </h4>
            {vendor.esg && vendor.esg.score !== undefined ? <div className="mt-1" data-id="element-18943">
                <div className="flex items-center" data-id="element-18944">
                  <BarChartIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-500" data-id="element-18945" />
                  <span className="text-gray-900 font-medium" data-id="element-18946">
                    {vendor.esg.score}
                  </span>
                  <span className="text-xs text-gray-500 ml-1" data-id="element-18947">/100</span>
                  {(() => {
              const {
                label,
                color
              } = getESGStatusInfo(vendor.esg.score);
              return <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`} data-id="element-18948">
                        {label}
                      </span>;
            })()}
                </div>
              </div> : <div className="mt-1 text-sm text-gray-500" data-id="element-18949">Not Provided</div>}
          </div>}
        {vendor.contacts && vendor.contacts.length > 0 && <div className="mt-4" data-id="element-18950">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18951">
              Primary Contact
            </h4>
            <div className="mt-1 flex items-start" data-id="element-18952">
              <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400 mt-0.5" data-id="element-18953" />
              <div data-id="element-18954">
                <p className="text-sm text-gray-900" data-id="element-18955">
                  {vendor.contacts[0].name}
                </p>
                <p className="text-xs text-gray-500" data-id="element-18956">
                  {vendor.contacts[0].email}
                </p>
              </div>
            </div>
          </div>}
      </div>
      {/* Card Footer */}
      <div className="px-4 py-4 sm:px-6 bg-gray-50 border-t border-gray-200" data-id="element-18957">
        <div className="flex justify-end" data-id="element-18958">
          <button onClick={() => onViewVendor(vendor)} className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-5 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18959">
            <EyeIcon className="h-4 w-4 mr-1" data-id="element-18960" />
            View Details
          </button>
        </div>
      </div>
    </div>;
}